import { useCallback, useEffect, useState } from 'react';
import { extractApiError } from '../../../lib/api';
import {
  rbacApi,
  type CompanyRbacOverview,
  type CompanyRoleView,
  type PermissionDefinitionView,
  type RoleAssignmentView,
} from '../../../lib/rbac';

export interface CompanyRbacData {
  permissions: PermissionDefinitionView[];
  roles: CompanyRoleView[];
  assignments: RoleAssignmentView[];
  overview: CompanyRbacOverview | null;
}

const EMPTY: CompanyRbacData = { permissions: [], roles: [], assignments: [], overview: null };

/**
 * 「역할·선임」 화면 데이터 — 권한 정의·역할·배정·개요 넷을 한 번에 싣는다(설정 페이지 선례
 * `Approval.tsx`와 같은 useState/useEffect 패턴 — 설정 화면은 react-query를 쓰지 않는다).
 *
 * 쓰기 뒤 재조회는 축별로: 배정 변경은 `assignments`+`roles`(holderCount)+`overview`(guidance holderCount),
 * 역할 변경은 `roles`, 개요 저장은 응답을 그대로 반영. `isCancelled`는 조직 전환 뒤 도착한 이전 조직
 * 응답을 버리는 가드(`Approval.tsx` W-h와 같은 이유).
 */
export function useCompanyRbac(companyId: string | undefined) {
  const [data, setData] = useState<CompanyRbacData>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAll = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!companyId) return;
      setIsLoading(true);
      setError('');
      try {
        const [permissions, roles, assignments, overview] = await Promise.all([
          rbacApi.getPermissions(companyId),
          rbacApi.getRoles(companyId),
          rbacApi.getAssignments(companyId),
          rbacApi.getOverview(companyId),
        ]);
        if (isCancelled()) return;
        setData({ permissions, roles, assignments, overview });
      } catch (err) {
        if (isCancelled()) return;
        setError(extractApiError(err).message);
      } finally {
        if (!isCancelled()) setIsLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    loadAll(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [companyId, loadAll]);

  /** 배정 변경 뒤 — 배정 목록·역할 보유자 수·안내 보유자 수가 함께 바뀐다. */
  const reloadAssignments = useCallback(async () => {
    if (!companyId) return;
    const [roles, assignments, overview] = await Promise.all([
      rbacApi.getRoles(companyId),
      rbacApi.getAssignments(companyId),
      rbacApi.getOverview(companyId),
    ]);
    setData((prev) => ({ ...prev, roles, assignments, overview }));
  }, [companyId]);

  /** 역할 생성·권한 변경·되돌리기·삭제 뒤. */
  const reloadRoles = useCallback(async () => {
    if (!companyId) return;
    const roles = await rbacApi.getRoles(companyId);
    setData((prev) => ({ ...prev, roles }));
  }, [companyId]);

  const setOverview = useCallback((overview: CompanyRbacOverview) => {
    setData((prev) => ({ ...prev, overview }));
  }, []);

  return { ...data, isLoading, error, reloadAll: loadAll, reloadAssignments, reloadRoles, setOverview };
}
